import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useCountries } from '../../api/useCountries';
import { useLocalContentLanguages } from '../../../../api/local-content/collection/product/useLocalContentLanguages';
import { ContainerInnerWithColumns } from '../../../../shared/common/ContainerInner';
import { LoadingWrapper } from '../../../../shared/components/LoadingWrapper';
import LoadingOverlay from '../../../../shared/components/LoadingOverlay';
import {
    elementWrapperMediumWidth,
    elementWrapperSmallWidth,
    StAddButtonWrapper,
    StAddFormField,
    StDetailViewWrapper,
    StDisableInputDropdown,
    StElementWrapper,
    StEnableDisableWrapper,
    StInfoSharedCollectionTitle,
    StLCProductDetailMetaData,
    StLeftCol,
    StMetadataTitleWrapperEditState,
    StPairRow,
    StRightCol,
    StSpacerHorizontal,
    StSpacerVertical,
    StStandardGenreWarning,
    StTitle,
    StTitleButtonWrapper,
    StTitleStatusWrapper
} from '../styled/LCStyledComponents';
import isEmpty from 'lodash/isEmpty';
import LCStatusIndicator from '../LCStatusIndicator';
import LCStatusActions from '../LCStatusActions';
import isNil from 'lodash/isNil';
import { Button, ButtonSize, ButtonTheme } from '../../../../shared/common/Button';
import { extractStateObject } from '../../../../shared/utils/utils-localcontent';
import { useLocalContentPerformers } from '../../../../api/local-content/collection/product/useLocalContentPerformers';
import { AuthorListPopup } from '../AuthorListPopup';
import find from 'lodash/find';
import { LCPerformerPopup } from '../../performer/LCPerformerPopup';
import { LCFormField } from '../../shared/LCFormField';
import { LCFormFieldInputText } from '../../shared/LCFormFieldInputText';
import HorizontalRuler from '../../../../shared/common/HorizontalRuler';
import { LCFormFieldValueList } from '../../shared/LCFormFieldValueList';
import { LCFormFieldValueText } from '../../shared/LCFormFieldValueText';
import LCFormFieldInputTextarea from '../../shared/LCFormFieldInputTextarea';
import LCFormFieldInputDropdown from '../../shared/LCFormFieldInputDropdown';
import { DropdownWithSearch } from '../../../../shared/common/DropdownWithSearch';
import startsWith from 'lodash/startsWith';
import toLower from 'lodash/toLower';
import { LCGenreDropdownWithSearch } from '../LCGenreDropdownWithSearch';
import { useUpdateForm } from '../../../../api/local-content/collection/product/useUpdateForm';
import { useUploadLocalContentProduct } from '../../../../api/local-content/collection/product/useUploadLocalContentProduct';
import { TYPE_ERROR, TYPE_WARNING } from '../../../../shared/notification/actions';
import { useUploadLocalContentPerformer } from '../../../../api/local-content/collection/product/useUploadLocalContentPerformer';
import { isStringAValidUrl } from '../../../../shared/utils/utils-validation-helpers';
import {
    checkForIncompletePerformerData,
    checkForIncompleteProductData,
    checkForInvalidProductData
} from '../api/mappings/productValidation';
import { KeywordInput } from '../KeywordInput';
import { Checkbox } from '../../../../shared/common/Checkbox';
import { Trans, useTranslation } from 'react-i18next';
import { usePreviousProps } from '../../../../hooks/usePreviousProps';
import { formatDate, formatDateToISOFormat } from '../../../../shared/utils/formatDate';
import { useAuthentication } from '../../../../app-store/authentication/useAuthentication';
import { showNotification } from '../../../../shared/notification/toastNotification';
import { useNavigate } from 'react-router-dom';
import { LibraryCreatedProductRequest } from '../../../../store/apiLibx';
import { hasLCManageRole } from '../../../../app-store/authentication/auth-utils';
import { useKeycloak } from '../../../../../useKeycloakAndInit';

const spaceBetweenBlocksInPx = 130;

export const LCProductDetailEditPage = () => {
    const {
        t,
        i18n: { language: currentLanguage }
    } = useTranslation();
    const navigate = useNavigate();
    const { id = '' } = useParams<'id'>();
    const [showCreatePerformerPopup, setShowCreatePerformerPopup] = useState(false);
    const [languageQuery, setLanguageQuery] = useState('');
    const { data: countries, isLoading: isCountriesLoading } = useCountries({});
    const { data: languages, isLoading: isLanguagesLoading } = useLocalContentLanguages();
    const { data: performers, isLoading: isPerformersLoading } = useLocalContentPerformers();
    const { mutate: localContentProductMutation } = useUploadLocalContentProduct();
    const kc = useKeycloak();

    const previousLanguage = usePreviousProps(currentLanguage);

    const {
        setProductStateWithValue,
        productState,
        onRemovePerformer,
        onAddPerformer,
        setPages,
        productFormat,
        authors,
        language,
        explicit,
        fiction,
        genres,
        ageGroup,
        ageCategory,
        setAgeCategory,
        setFiction,
        setGenreCategory,
        addLocalGenre,
        removeLocalGenre,
        addStandardGenre,
        removeStandardGenre,
        genreCategory,
        onRemoveKeyword,
        onAddKeyword,
        setReleaseDate,
        isProductLoading,
        setSeriesNumber,
        localGenreSearchQuery,
        setLocalGenreSearchQuery,
        standardGenreSearchQuery,
        setStandardGenreSearchQuery,
        isLocalContentGenresLoading,
        isStandardGenresLoading,
        localGenreList,
        standardGenreList,
        isCategoriesLoading,
        showAddPerformerPopup,
        setShowAddPerformerPopup,
        setExplicit,
        setLanguage
    } = useUpdateForm(id);

    const loading =
        isCountriesLoading ||
        isLanguagesLoading ||
        isPerformersLoading ||
        isProductLoading ||
        isLocalContentGenresLoading ||
        isStandardGenresLoading ||
        isCategoriesLoading;

    function submitForm() {
        const product: LibraryCreatedProductRequest & { id: string } = {
            ...productState,
            id,
            genreIds: [
                ...genres.localGenres.map(({ id: genreId }) => genreId),
                ...genres.standardGenres.map(({ id: genreId }) => genreId)
            ],
            authorIds: authors?.map(x => x.id),
            ageGroup: ageGroup.current,
            ageCategory: ageCategory.current,
            fiction: fiction.current,
            explicit: explicit.current,
            productFormat: productFormat.current,
            genreCategoryId: genreCategory.current,
            languageCode: language.languageCode,
            releaseDate: formatDateToISOFormat(productState.releaseDate, currentLanguage),
            seriesName: productState.seriesName ? productState.seriesName : undefined,
            seriesNumber: productState.seriesNumber ? productState.seriesNumber : undefined
        };

        const invalidProductData = checkForInvalidProductData(productState);
        if (invalidProductData.length > 0) {
            const invalidFields = invalidProductData.join(', ');
            showNotification(
                t('yourLocalContent.yourTitles.warnings.invalidData', { invalidFields: invalidFields }),
                TYPE_ERROR,
                15000
            );
            return;
        }

        const incompleteProductData = checkForIncompleteProductData(product, genres, {
            localGenres: localGenreList,
            standardGenres: standardGenreList
        });
        if (incompleteProductData.length > 0) {
            const invalidFields = incompleteProductData.join(', ');
            showNotification(
                t('yourLocalContent.yourTitles.warnings.incompleteData', { invalidFields: invalidFields }),
                TYPE_ERROR,
                15000
            );
            return;
        }

        const removedParentGenres = [
            ...genres.localGenres.filter(x => !!genres.localGenres.find(y => y.id.startsWith(`${x.id}->`))),
            ...genres.standardGenres.filter(x => !!genres.standardGenres.find(y => y.id.startsWith(`${x.id}->`)))
        ];

        if (removedParentGenres.length > 0) {
            showNotification(
                `${t('yourLocalContent.yourTitles.warnings.removedParentGenres', {
                    removedParentGenres: removedParentGenres.map(x => x.name).join(', ')
                })}`,
                TYPE_WARNING,
                15000
            );
        }

        if (product.ageGroup === 'All') {
            product.ageGroup = undefined;
        }

        localContentProductMutation(product);
    }

    const localisedDate = useMemo(
        () =>
            productState.releaseDate.length === 10
                ? formatDate(productState.releaseDate, previousLanguage ?? '', currentLanguage)
                : productState.releaseDate,
        [productState.releaseDate, previousLanguage, currentLanguage]
    );

    const isGenreCategoryEmpty = genreCategory.current === undefined;
    const isMarcRecordEditable = productState.enableMarcRecord ?? false;

    const isLocalGenreDisabled =
        (localGenreList.genres.length < 1 && isEmpty(localGenreSearchQuery) && !isLocalContentGenresLoading) ||
        isGenreCategoryEmpty;

    const isStandardGenreDisabled =
        (standardGenreList.genres.length < 0 && isEmpty(standardGenreSearchQuery) && !isStandardGenresLoading) ||
        isGenreCategoryEmpty;
    const {
        spoc: { id: spocIdCurrentUser }
    } = useAuthentication();
    const spocIdPublisherProduct = productState.spocId;
    const isNew = false;
    const titleText = isNew ? t('yourLocalContent.yourTitles.newTitle') : productState.titleWithoutPrefix;
    const isReadOnly = spocIdPublisherProduct !== spocIdCurrentUser;
    let disabled: boolean;
    const isEditMode = true;
    const userHasLCManageRole = hasLCManageRole(kc);
    const statusActionsDisabled = isEditMode || !userHasLCManageRole;
    const activeStatus = productState.activeStatus ?? [];
    const stateList = extractStateObject(activeStatus) ?? [];
    const liveLibx = productState.liveOnLibXchange ?? false;
    const shareLibx = productState.shareOnLibXchange ?? false;

    const handleCancel = () => {
        navigate('..');
    };
    const { newState, updated, processing, liveCollection, inactive } = stateList;
    disabled = inactive || isEditMode;

    const { mutate: uploadPerformerMutation } = useUploadLocalContentPerformer();

    const onSavePerformer = (performer, performerPhoto) => {
        const websiteUrl = performer['website'] ?? '';
        const incompletePerformerData = checkForIncompletePerformerData(performer);

        if (websiteUrl && !isStringAValidUrl(websiteUrl)) {
            showNotification(t('yourLocalContent.yourTitles.warnings.invalidUrl'), TYPE_ERROR, 15000);
            return Promise.reject(0);
        }
        if (incompletePerformerData.length > 0) {
            const invalidFields = incompletePerformerData.join(', ');
            showNotification(
                t('yourLocalContent.yourTitles.warnings.incompleteData', { invalidFields: invalidFields }),
                TYPE_ERROR,
                15000
            );
            return Promise.reject(0);
        }

        uploadPerformerMutation({ performer, photo: performerPhoto });
        setShowCreatePerformerPopup(false);
    };

    return (
        <ContainerInnerWithColumns>
            <LoadingWrapper>
                <LoadingOverlay loading={loading} />
            </LoadingWrapper>

            <StTitleStatusWrapper>
                <div>
                    <a className="fl-left" onClick={() => window.history.back()}>
                        <i className="material-icons" style={{ fontSize: '28px' }}>
                            arrow_back
                        </i>
                    </a>
                    <span style={{ fontSize: '20px' }}>{titleText}</span>
                </div>

                {!isEmpty(stateList) ? (
                    <>
                        <LCStatusIndicator
                            newUpdated={newState || updated || processing}
                            processing={processing}
                            liveCollection={liveCollection}
                            inactive={inactive}
                            liveLibx={liveLibx ?? false}
                            disabled={disabled}
                        />

                        {!isReadOnly ? (
                            <LCStatusActions
                                newState={newState}
                                updated={updated}
                                liveCollection={liveCollection}
                                inactive={inactive}
                                liveLibx={liveLibx ?? false}
                                shareLibx={shareLibx ?? false}
                                productId={productState.id ?? ''}
                                disabled={statusActionsDisabled}
                                isAssetUploaded={!isNil(productState.assetFileName)}
                            />
                        ) : null}
                        {isReadOnly ? (
                            <StInfoSharedCollectionTitle>Shared Collection Title</StInfoSharedCollectionTitle>
                        ) : null}
                    </>
                ) : null}
            </StTitleStatusWrapper>
            <StDetailViewWrapper className="LCProductDetailView" assetWrapperHidden={isEditMode}>
                <div>
                    <StTitleButtonWrapper>
                        <StMetadataTitleWrapperEditState>
                            <StTitle>{t('yourLocalContent.yourTitles.metadata')}</StTitle>
                            <Button
                                size={ButtonSize.LARGE}
                                theme={ButtonTheme.NEUTRAL}
                                text={t('yourLocalContent.yourTitles.cancel')}
                                onClick={handleCancel}
                            />
                            <Button
                                size={ButtonSize.LARGE}
                                text={t('yourLocalContent.yourTitles.save')}
                                onClick={submitForm}
                            />
                        </StMetadataTitleWrapperEditState>
                    </StTitleButtonWrapper>
                    <>
                        <AuthorListPopup
                            show={showAddPerformerPopup}
                            performers={performers || []}
                            selectedPerformers={(performers || []).filter(p =>
                                find(productState.authors, { id: p.id })
                            )}
                            onAbort={() => setShowAddPerformerPopup(false)}
                            onAddPerformer={onAddPerformer}
                            onCreatePerformer={() => setShowCreatePerformerPopup(true)}
                            countries={countries ?? []}
                        />
                        <LCPerformerPopup
                            show={showCreatePerformerPopup}
                            isNew={true}
                            onSuccess={onSavePerformer}
                            onAbort={() => setShowCreatePerformerPopup(false)}
                        />
                        <StLCProductDetailMetaData>
                            <StRightCol>
                                <StElementWrapper width={elementWrapperSmallWidth}>
                                    <LCFormField
                                        title={t('yourLocalContent.yourTitles.titlePrefix')}
                                        tooltipText={t('yourLocalContent.yourTitles.tooltips.titlePrefix')}
                                        isEdit={true}
                                    >
                                        <LCFormFieldInputText
                                            value={productState.titlePrefix}
                                            maxLength={10}
                                            onValueChange={value => setProductStateWithValue('titlePrefix', value)}
                                        />
                                    </LCFormField>
                                </StElementWrapper>
                                <StSpacerHorizontal />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.titleWithoutPrefix')}
                                    isEdit={true}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.titleWithoutPrefix')}
                                    mandatory
                                >
                                    <LCFormFieldInputText
                                        value={productState.titleWithoutPrefix}
                                        maxLength={240}
                                        onValueChange={value => setProductStateWithValue('titleWithoutPrefix', value)}
                                        bold
                                    />
                                </LCFormField>
                                <StSpacerHorizontal />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.subtitle')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.subtitle')}
                                    isEdit={true}
                                >
                                    <LCFormFieldInputText
                                        value={productState.subTitle}
                                        maxLength={240}
                                        onValueChange={value => setProductStateWithValue('subTitle', value)}
                                    />
                                </LCFormField>
                                <StSpacerHorizontal />
                                <StPairRow>
                                    <LCFormField
                                        title={t('yourLocalContent.yourTitles.seriesName')}
                                        isEdit={true}
                                        tooltipText={t('yourLocalContent.yourTitles.tooltips.seriesName')}
                                    >
                                        <LCFormFieldInputText
                                            value={productState.seriesName}
                                            maxLength={240}
                                            onValueChange={value => setProductStateWithValue('seriesName', value)}
                                        />
                                    </LCFormField>

                                    <StSpacerVertical />
                                    <StElementWrapper width={elementWrapperSmallWidth}>
                                        <LCFormField
                                            title={t('yourLocalContent.yourTitles.seriesNumber')}
                                            tooltipText={t('yourLocalContent.yourTitles.tooltips.seriesNumber')}
                                            isEdit={true}
                                        >
                                            <LCFormFieldInputText
                                                type="number"
                                                min={1}
                                                step={1}
                                                value={productState.seriesNumber}
                                                onValueChange={setSeriesNumber}
                                                error={productState.seriesNumberError}
                                            />
                                        </LCFormField>
                                    </StElementWrapper>
                                </StPairRow>
                                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />
                                <StAddFormField
                                    title={t('yourLocalContent.yourTitles.authors')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.authors')}
                                    isEdit={true}
                                    mandatory
                                >
                                    <LCFormFieldValueList
                                        value={authors}
                                        onRemove={onRemovePerformer}
                                        editable={true}
                                        bold
                                    />
                                </StAddFormField>
                                <StAddButtonWrapper>
                                    <a
                                        className="bt bt-green material-link"
                                        onClick={() => setShowAddPerformerPopup(true)}
                                        style={{ display: 'inline-flex' }}
                                    >
                                        <i className="material-icons">add</i>
                                        <span>{t('yourLocalContent.yourTitles.addAuthor')}</span>
                                    </a>
                                </StAddButtonWrapper>
                                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />

                                <StPairRow>
                                    <StElementWrapper width={elementWrapperSmallWidth}>
                                        <LCFormField
                                            title={t('yourLocalContent.yourTitles.bsin')}
                                            tooltipText={t('yourLocalContent.yourTitles.tooltips.bsin')}
                                            isEdit={true}
                                        >
                                            <LCFormFieldValueText
                                                placeholder={t('yourLocalContent.yourTitles.generated')}
                                                text={productState.id}
                                            />
                                        </LCFormField>
                                    </StElementWrapper>
                                    <StSpacerVertical />
                                    <StElementWrapper width={elementWrapperMediumWidth}>
                                        <LCFormField
                                            title={t('yourLocalContent.yourTitles.alternativeId')}
                                            tooltipText={t('yourLocalContent.yourTitles.tooltips.alternativeId')}
                                            isEdit={true}
                                        >
                                            <LCFormFieldValueText
                                                placeholder={
                                                    <LCFormFieldInputTextarea
                                                        value={productState.alternativeId}
                                                        onValueChange={value =>
                                                            setProductStateWithValue('alternativeId', value)
                                                        }
                                                    />
                                                }
                                            />
                                        </LCFormField>
                                    </StElementWrapper>
                                </StPairRow>
                                <StSpacerHorizontal />
                                <StPairRow>
                                    <StElementWrapper width={elementWrapperSmallWidth}>
                                        <LCFormField
                                            title={t('yourLocalContent.yourTitles.format')}
                                            isEdit={true}
                                            tooltipText={t('yourLocalContent.yourTitles.tooltips.format')}
                                            mandatory
                                        >
                                            <LCFormFieldInputDropdown
                                                options={productFormat}
                                                onValueChange={currentKey =>
                                                    setProductStateWithValue('productFormat', currentKey)
                                                }
                                            />
                                        </LCFormField>
                                    </StElementWrapper>
                                    <StSpacerVertical />
                                    <StElementWrapper width={elementWrapperMediumWidth}>
                                        <LCFormField
                                            title={t('yourLocalContent.yourTitles.numPages')}
                                            tooltipText={t('yourLocalContent.yourTitles.tooltips.numPages')}
                                            isEdit={true}
                                            mandatory
                                        >
                                            <LCFormFieldInputText
                                                type="number"
                                                min={1}
                                                step={1}
                                                value={productState.pages}
                                                onValueChange={setPages}
                                                error={productState.pagesError}
                                            />
                                        </LCFormField>
                                    </StElementWrapper>
                                </StPairRow>
                                <StSpacerHorizontal />
                                <StPairRow>
                                    <StElementWrapper width={elementWrapperMediumWidth}>
                                        <LCFormField
                                            title={t('yourLocalContent.yourTitles.language')}
                                            tooltipText={t('yourLocalContent.yourTitles.tooltips.language')}
                                            isEdit={true}
                                            mandatory
                                        >
                                            <DropdownWithSearch
                                                items={
                                                    languages?.filter(item =>
                                                        startsWith(toLower(item.name), toLower(languageQuery))
                                                    ) ?? []
                                                }
                                                selectedItem={language.language}
                                                itemKey={'code'}
                                                minWidth={150}
                                                handleItemClick={value => setLanguage(value.name, value.code)}
                                                onClear={() => setLanguageQuery('')}
                                                onClose={() => setLanguageQuery('')}
                                                onSearch={setLanguageQuery}
                                            />
                                        </LCFormField>
                                    </StElementWrapper>
                                </StPairRow>
                                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />

                                <LCFormField
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.providedBy')}
                                    title={t('yourLocalContent.yourTitles.providedBy')}
                                    isEdit={true}
                                >
                                    <LCFormFieldValueText
                                        placeholder={t('yourLocalContent.yourTitles.generated')}
                                        text={productState.publisher}
                                    />
                                </LCFormField>
                                <StSpacerHorizontal />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.publishedBy')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.publishedBy')}
                                    isEdit={true}
                                    mandatory
                                >
                                    <LCFormFieldInputText
                                        value={productState.publisherImprint}
                                        maxLength={480}
                                        onValueChange={value => setProductStateWithValue('publisherImprint', value)}
                                    />
                                </LCFormField>
                                <StSpacerHorizontal />
                                <StElementWrapper width={elementWrapperMediumWidth}>
                                    <LCFormField
                                        tooltipText={t('yourLocalContent.yourTitles.tooltips.releaseDate')}
                                        title={t('yourLocalContent.yourTitles.releaseDate')}
                                        isEdit={true}
                                        mandatory
                                    >
                                        <LCFormFieldInputText
                                            value={localisedDate}
                                            onValueChange={setReleaseDate}
                                            error={productState.releaseDateError}
                                        />
                                    </LCFormField>
                                    {productState.releaseDateError ? (
                                        <small className="form-error">
                                            {t('yourLocalContent.yourTitles.warnings.releaseDateError')}
                                        </small>
                                    ) : null}
                                </StElementWrapper>
                            </StRightCol>
                            <StLeftCol>
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.teaser')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.teaser')}
                                    isEdit={true}
                                    mandatory
                                >
                                    <LCFormFieldInputText
                                        value={productState.teaser}
                                        maxLength={160}
                                        onValueChange={value => setProductStateWithValue('teaser', value)}
                                    />
                                </LCFormField>

                                <StSpacerHorizontal />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.description')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.description')}
                                    isEdit={true}
                                    mandatory
                                >
                                    <LCFormFieldInputTextarea
                                        value={productState.description}
                                        maxLength={3500}
                                        onValueChange={value => setProductStateWithValue('description', value)}
                                        textAreaMaxHeight={600}
                                    />
                                </LCFormField>
                                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />

                                <StPairRow>
                                    <StElementWrapper width={elementWrapperSmallWidth}>
                                        <LCFormField
                                            title={t('yourLocalContent.yourTitles.ageCategory')}
                                            tooltipText={t('yourLocalContent.yourTitles.tooltips.ageCategory')}
                                            isEdit={true}
                                            mandatory
                                        >
                                            <LCFormFieldInputDropdown
                                                options={ageCategory}
                                                onValueChange={setAgeCategory}
                                            />
                                        </LCFormField>
                                    </StElementWrapper>
                                    <StSpacerVertical />
                                    <StElementWrapper width={elementWrapperSmallWidth}>
                                        <LCFormField
                                            title={t('yourLocalContent.yourTitles.ageGroup')}
                                            tooltipText={t('yourLocalContent.yourTitles.tooltips.ageGroup')}
                                            isEdit={true}
                                            mandatory
                                        >
                                            <LCFormFieldInputDropdown
                                                options={ageGroup}
                                                onValueChange={key => {
                                                    setProductStateWithValue('ageGroup', key);
                                                }}
                                            />
                                        </LCFormField>
                                    </StElementWrapper>
                                </StPairRow>
                                <StSpacerHorizontal />

                                <StPairRow>
                                    <StElementWrapper width={elementWrapperSmallWidth}>
                                        <LCFormField
                                            title={t('yourLocalContent.yourTitles.fiction')}
                                            tooltipText={t('yourLocalContent.yourTitles.tooltips.fiction')}
                                            isEdit={true}
                                            mandatory
                                        >
                                            <LCFormFieldInputDropdown options={fiction} onValueChange={setFiction} />
                                        </LCFormField>
                                    </StElementWrapper>
                                    <StSpacerVertical />
                                    <StElementWrapper width={elementWrapperSmallWidth}>
                                        <LCFormField
                                            title={t('yourLocalContent.yourTitles.explicit')}
                                            mandatory
                                            tooltipText={t('yourLocalContent.yourTitles.tooltips.explicit')}
                                            isEdit={true}
                                        >
                                            <LCFormFieldInputDropdown options={explicit} onValueChange={setExplicit} />
                                        </LCFormField>
                                    </StElementWrapper>
                                </StPairRow>
                                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.localContentCategory')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.localContentCategory')}
                                    isEdit={true}
                                    mandatory
                                >
                                    <StDisableInputDropdown
                                        options={genreCategory}
                                        onValueChange={setGenreCategory}
                                        disabled={isNil(fiction.current)}
                                    />
                                </LCFormField>
                                <StSpacerHorizontal />
                                <StEnableDisableWrapper disabled={isLocalGenreDisabled}>
                                    <StAddFormField
                                        title={t('yourLocalContent.yourTitles.localGenre')}
                                        tooltipText={t('yourLocalContent.yourTitles.tooltips.localGenre')}
                                        isEdit={true}
                                        mandatory={localGenreList.mandatory}
                                    >
                                        <LCFormFieldValueList
                                            value={genres.localGenres}
                                            onRemove={removeLocalGenre}
                                            editable={true}
                                        />
                                    </StAddFormField>
                                    <StAddButtonWrapper>
                                        <LCGenreDropdownWithSearch
                                            genres={localGenreList.genres}
                                            selectedGenres={genres.localGenres}
                                            addGenre={addLocalGenre}
                                            fetchGenres={setLocalGenreSearchQuery}
                                            disabled={isLocalGenreDisabled}
                                        />
                                    </StAddButtonWrapper>
                                </StEnableDisableWrapper>
                                <StSpacerHorizontal />
                                <StEnableDisableWrapper disabled={isStandardGenreDisabled}>
                                    <StAddFormField
                                        title={t('yourLocalContent.yourTitles.standardBorrowBoxGenres')}
                                        tooltipText={t('yourLocalContent.yourTitles.tooltips.standardBorrowBoxGenres')}
                                        isEdit={true}
                                        mandatory={standardGenreList.mandatory}
                                    >
                                        <>
                                            {!isStandardGenreDisabled ? (
                                                <StStandardGenreWarning>
                                                    <Trans i18nKey="yourLocalContent.yourTitles.warnings.standardGenreWarning" />
                                                </StStandardGenreWarning>
                                            ) : null}
                                            <LCFormFieldValueList
                                                value={genres.standardGenres}
                                                onRemove={removeStandardGenre}
                                                editable={true}
                                            />
                                        </>
                                    </StAddFormField>
                                    <StAddButtonWrapper>
                                        <LCGenreDropdownWithSearch
                                            genres={standardGenreList.genres}
                                            selectedGenres={genres.standardGenres}
                                            addGenre={addStandardGenre}
                                            fetchGenres={setStandardGenreSearchQuery}
                                            disabled={isStandardGenreDisabled}
                                        />
                                    </StAddButtonWrapper>
                                </StEnableDisableWrapper>
                                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />

                                <KeywordInput
                                    keywords={productState.keywords ?? []}
                                    isEditable={true}
                                    onRemoveKeyword={onRemoveKeyword}
                                    onAddKeyword={onAddKeyword}
                                />
                                <HorizontalRuler heightSpacer={spaceBetweenBlocksInPx} />

                                <LCFormField title="&#160;" isEdit={true}>
                                    <Checkbox
                                        label={t('yourLocalContent.yourTitles.enableMarcRecords')}
                                        checked={productState.enableMarcRecord ?? false}
                                        disabled={false}
                                        onChange={value => setProductStateWithValue('enableMarcRecord', value)}
                                    />
                                </LCFormField>

                                <StSpacerHorizontal />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.marcTitle')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.marcTitle')}
                                    isEdit={isMarcRecordEditable}
                                    mandatory={productState.enableMarcRecord ?? false}
                                >
                                    <LCFormFieldInputText
                                        value={productState.marcTitleWithoutPrefix}
                                        onValueChange={value =>
                                            setProductStateWithValue('marcTitleWithoutPrefix', value)
                                        }
                                    />
                                </LCFormField>

                                <StSpacerHorizontal />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.marcSubjectHeadings')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.marcSubjectHeadings')}
                                    isEdit={isMarcRecordEditable}
                                    mandatory={productState.enableMarcRecord ?? false}
                                >
                                    <LCFormFieldInputTextarea
                                        value={productState.marcSubjectHeadings}
                                        onValueChange={value => setProductStateWithValue('marcSubjectHeadings', value)}
                                        maxLength={240}
                                    />
                                </LCFormField>
                                <StSpacerHorizontal />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.marcGenreHeadings')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.marcGenreHeadings')}
                                    isEdit={isMarcRecordEditable}
                                >
                                    <LCFormFieldInputText
                                        value={productState.marcGenreHeadings}
                                        onValueChange={value => setProductStateWithValue('marcGenreHeadings', value)}
                                    />
                                </LCFormField>
                                <StSpacerHorizontal />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.marcGeographicalHeadings')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.marcGeographicalHeadings')}
                                    isEdit={isMarcRecordEditable}
                                >
                                    <LCFormFieldInputTextarea
                                        value={productState.marcGeographicSubjects}
                                        onValueChange={value =>
                                            setProductStateWithValue('marcGeographicSubjects', value)
                                        }
                                    />
                                </LCFormField>
                                <StSpacerHorizontal />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.marcBiographicalSubjectAuthorisedName')}
                                    tooltipText={t(
                                        'yourLocalContent.yourTitles.tooltips.marcBiographicalSubjectAuthorisedName'
                                    )}
                                    isEdit={isMarcRecordEditable}
                                >
                                    <LCFormFieldInputTextarea
                                        value={productState.marcBiographicSubjects}
                                        onValueChange={value =>
                                            setProductStateWithValue('marcBiographicSubjects', value)
                                        }
                                    />
                                </LCFormField>
                                <StSpacerHorizontal />
                                <LCFormField
                                    title={t('yourLocalContent.yourTitles.marcSeriesAuthorisedName')}
                                    tooltipText={t('yourLocalContent.yourTitles.tooltips.marcSeriesAuthorisedName')}
                                    isEdit={isMarcRecordEditable}
                                >
                                    <LCFormFieldInputTextarea
                                        value={productState.marcSeries}
                                        onValueChange={value => setProductStateWithValue('marcSeries', value)}
                                    />
                                </LCFormField>
                            </StLeftCol>
                        </StLCProductDetailMetaData>
                    </>
                </div>
            </StDetailViewWrapper>
        </ContainerInnerWithColumns>
    );
};
